/**
 * Client half of the host poll cluster's conditional requests (ENG-9584).
 *
 * The manager echoes back the `payload_token` the API stamped on the last
 * response for an agent; the API answers `{ unchanged: true }` when the payload
 * it would send right now is byte-identical. See
 * `packages/api/src/lib/conditional-payload.ts` for why the hash is computed on
 * exactly one side of the wire and why the marker is a body field rather than a
 * `304`.
 *
 * WHAT LIVES HERE AND WHY. The saving itself is two lines at the call site. The
 * part that is easy to get wrong — and that decides whether an agent's memory
 * corpus goes missing — is what to do when the marker arrives and there is no
 * body to apply it to. That is reachable without any server misbehaviour:
 * `clearAgentCaches` and `markAgentForFreshMemorySync` both run off Realtime
 * events and can purge the cache entry while the request that quoted its token
 * is still in flight. The tempting reading of `unchanged` in that state is
 * "nothing to do", and it is the dangerous one, because "the corpus did not
 * change" and "I have no corpus" are the same shape and opposite facts.
 *
 * So the decision is a pure function with three outcomes and no default branch,
 * and the caller is forced to name what it does about `no-body` rather than
 * fall through it.
 */

/** The wire shape: either the full payload, or the unchanged marker. */
export interface ConditionalWire {
  unchanged?: unknown;
  payload_token?: unknown;
}

/** A cached body and the server token that describes it. */
export interface ConditionalCacheEntry<T> {
  hash: string;
  body: T;
}

/**
 * The entry a settled response may actually be applied to (ENG-9584, CodeRabbit
 * on PR #5220).
 *
 * The token is quoted BEFORE the request and the answer arrives after it, so
 * the caller inevitably holds a reference captured across an `await`. A purge
 * landing in that window — `clearAgentCaches` on a teardown,
 * `markAgentForFreshMemorySync` on a migration — leaves that local variable
 * perfectly non-null, so a caller testing its own captured value concludes it
 * still holds a body and applies an `unchanged` answer to state the purge
 * deliberately retired. The purge stops being authoritative precisely when it
 * matters most, and nothing reports it.
 *
 * So the caller re-READS the map and passes what it finds. The entry counts
 * only if it is still there AND still carries the token that was quoted: a
 * purge-then-repopulate by a concurrent pass is a different entry, and matching
 * on presence alone would silently accept it.
 *
 * This is the same shape as ENG-9585's `beginFetch`/`markFetched` token — decide
 * what a response may do from what was true when it LANDS, not only from what
 * was true when it was sent.
 */
export function stillHeld<T>(
  quoted: ConditionalCacheEntry<T> | undefined,
  live: ConditionalCacheEntry<T> | undefined,
): ConditionalCacheEntry<T> | undefined {
  if (!quoted || !live) return undefined;
  return live.hash === quoted.hash ? live : undefined;
}

export type ConditionalDecision =
  /** The marker matched a body we hold. That body is provably current. */
  | { use: 'cached' }
  /**
   * A full payload. `token` is the value to cache alongside it, or null when
   * the server stamped none — an API predating ENG-9584, or one that stopped
   * stamping. A null token means DROP the cache entry, never keep the previous
   * one: a stale hash is harmless against a server that ignores it and is not
   * harmless against one that starts honouring it again.
   */
  | { use: 'wire'; token: string | null }
  /**
   * The marker arrived with nothing to apply it to. The caller must re-fetch
   * without a token — it must NOT treat this as "no change" and it must not
   * substitute an empty payload.
   */
  | { use: 'no-body' };

/**
 * Decide what a conditional response means, given whether a cached body is
 * still held for that key.
 *
 * `unchanged` is compared with `=== true` rather than coerced. A truthy-but-not
 * -true value (a string `"false"`, a `1`, an object) is treated as a full
 * payload, which is the fail-open direction: the worst outcome is spending
 * bytes we could have saved, against the alternative of discarding a real
 * payload because some proxy or older API put something unexpected there.
 */
export function resolveConditionalResponse(
  wire: ConditionalWire,
  hasCachedBody: boolean,
): ConditionalDecision {
  if (wire.unchanged === true) {
    return hasCachedBody ? { use: 'cached' } : { use: 'no-body' };
  }
  const token = wire.payload_token;
  return {
    use: 'wire',
    // An empty string is rejected as well as a non-string: it would round-trip
    // as a token the server can never match, so it is indistinguishable from
    // absent and should be stored as absent rather than as a hash of "".
    token: typeof token === 'string' && token.length > 0 ? token : null,
  };
}
